/**
 * Pure helpers for the work-order → account owner auto-fill flow (issue #285).
 *
 * The work-order form (components/work-orders/form.tsx) matches the Job Location
 * to an account, then pulls the account owner's name & phone into the Owner
 * fields and offers to save a newly-entered phone back to the owner record.
 * All of the non-trivial decisions live here so the component stays thin and
 * the rules are unit-testable in isolation.
 */
import type { AccountPersonRole, Person, PersonType } from '@/types/models';
import { AccountPersonRole as Role } from '@/types/models';
import { personDisplayName } from '@/utils/person-display';

/** A person row as returned by `GET /people?accountId=` (carries the junction role + relationship). */
export type AccountOwnerPerson = Person & { role?: AccountPersonRole; relationship?: string };

/**
 * Pick the person whose name & phone should seed the work order: prefer the
 * explicit `owner` role, otherwise fall back to the first attached person who is not
 * merely an emergency contact (#569).
 */
export const pickOwnerPerson = (people: AccountOwnerPerson[] | null | undefined): AccountOwnerPerson | null => {
	if (!people || people.length === 0) return null;
	return people.find((p) => p.role === Role.Owner)
		?? people.find((p) => p.role !== Role.EmergencyContact)
		?? null;
};

/**
 * Join a person's first/last name into a single display string, falling back to
 * the company name for a company owner (whose personal name is blank) so the
 * work order's contact field isn't left empty. See `personDisplayName`.
 */
export const ownerFullName = (
	person: { firstName?: string; lastName?: string; companyName?: string; personType?: PersonType | string } | null | undefined,
): string => personDisplayName(person);

/**
 * An Owner field's value once the Job Location resolves to a different account (#384).
 *
 * It used to fill only a BLANK field, so picking the wrong address and then correcting it
 * left the wrong owner's name and phone on the work order — easy to miss and save. Now a
 * value the form auto-filled itself (`lastAutofilled`) is replaced by the new account's
 * owner, even with a blank when the new owner has none, since keeping the stale one attaches
 * the wrong person. Anything else non-blank was typed by the user (or saved on the work
 * order) and is kept.
 */
export const nextOwnerFieldValue = (current: string, lastAutofilled: string | null, incoming: string): string =>
	!current.trim() || current === lastAutofilled ? incoming : current;

/**
 * Split a free-text owner name into first/last for creating a new Person.
 * First token becomes `firstName`; the remainder (if any) becomes `lastName`.
 */
export const splitOwnerName = (name: string): { firstName: string; lastName?: string } => {
	const trimmed = (name ?? '').trim().replace(/\s+/g, ' ');
	if (!trimmed) return { firstName: '' };
	const parts = trimmed.split(' ');
	const firstName = parts.shift() as string;
	const lastName = parts.join(' ');
	return lastName ? { firstName, lastName } : { firstName };
};

/**
 * Whether to prompt "Save this phone number to the account?" on save: only when
 * an account is matched, a phone was entered for the work order, and the owner
 * record is either missing or has no phone of its own yet.
 */
export const shouldPromptSavePhone = (args: {
	matchedAccountId?: string | null;
	enteredPhone?: string | null;
	ownerPerson?: { phone?: string } | null;
}): boolean => {
	const { matchedAccountId, enteredPhone, ownerPerson } = args;
	if (!matchedAccountId) return false;
	if (!enteredPhone || !enteredPhone.trim()) return false;
	return !ownerPerson?.phone || !ownerPerson.phone.trim();
};
